import { Controller } from "@hotwired/stimulus"
import ahoy from 'ahoy.js'

// Connects to data-controller="home"
export default class extends Controller {
  connect() {
    this.pageLoadedAt = new Date()
    this.maxScrollDepth = 0
    this.lastTrackedDepth = 0
    this.scrollDebounceTimeout = null
    this.exitIntentTracked = false

    // Setup tracking listeners
    this.setupScrollTracking()
    this.setupExitIntent()
  }

  getDeviceType() {
    const width = window.innerWidth
    if (width < 640) return 'mobile'
    if (width < 1024) return 'tablet'
    return 'desktop'
  }

  // Scroll depth tracking with debounce - tracks max depth reached
  setupScrollTracking() {
    let ticking = false

    const trackScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const windowHeight = window.innerHeight
          const documentHeight = document.documentElement.scrollHeight
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop
          const scrollPercent = Math.round((scrollTop / (documentHeight - windowHeight)) * 100)

          // Update max scroll depth
          if (scrollPercent > this.maxScrollDepth) {
            this.maxScrollDepth = scrollPercent
          }

          // Clear existing debounce timeout
          if (this.scrollDebounceTimeout) {
            clearTimeout(this.scrollDebounceTimeout)
          }

          // Debounce: track max depth after scrolling stops
          this.scrollDebounceTimeout = setTimeout(() => {
            this.trackMaxScrollDepth()
          }, 500) // Wait 500ms after scroll stops

          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', trackScroll, { passive: true })
  }

  // Track the maximum scroll depth reached
  trackMaxScrollDepth() {
    // Milestones: 5% (initial interaction), then every 15%
    const milestones = [5, 20, 35, 50, 65, 80, 95]

    // Find the highest milestone reached that we haven't tracked yet
    let milestoneToTrack = null
    for (const milestone of milestones) {
      if (this.maxScrollDepth >= milestone && milestone > this.lastTrackedDepth) {
        milestoneToTrack = milestone
      }
    }

    // Track the highest milestone reached
    if (milestoneToTrack !== null) {
      this.lastTrackedDepth = milestoneToTrack
      ahoy.track('scroll_depth', {
        depth: milestoneToTrack,
        page: 'home',
        device_type: this.getDeviceType()
      })
    }
  }

  // Exit intent detection
  setupExitIntent() {
    // Track which sides have been tracked to avoid duplicate events
    this.exitSidesTracked = new Set()

    // Desktop: mouse leaving viewport
    const handleMouseLeave = (event) => {
      // Determine which side the mouse exited from
      const exitSide = this.detectExitSide(event)

      if (exitSide && !this.exitSidesTracked.has(exitSide)) {
        this.exitSidesTracked.add(exitSide)
        this.trackExitIntent('mouse_leave', exitSide)
      }
    }

    // Mobile & Desktop: page visibility change (tab switch, minimize, etc)
    const handleVisibilityChange = () => {
      if (document.hidden && !this.exitIntentTracked) {
        // Only track if user spent at least 1 second on page
        const timeOnPage = (new Date() - this.pageLoadedAt) / 1000
        if (timeOnPage >= 1) {
          this.trackExitIntent('visibility_change', null)
        }
      }
    }

    // Mobile: beforeunload as fallback
    const handleBeforeUnload = () => {
      if (!this.exitIntentTracked) {
        const timeOnPage = (new Date() - this.pageLoadedAt) / 1000
        if (timeOnPage >= 1) {
          // Track max scroll depth one final time
          this.trackMaxScrollDepth()
          this.trackExitIntent('before_unload', null)
        }
      }
    }

    document.addEventListener('mouseout', handleMouseLeave)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)
  }

  detectExitSide(event) {
    // Check if mouse is actually leaving the document
    if (event.relatedTarget || event.toElement) {
      return null // Mouse is still within the document
    }

    const tolerance = 5 // Small tolerance for edge detection

    if (event.clientY <= tolerance) {
      return 'top'
    } else if (event.clientY >= window.innerHeight - tolerance) {
      return 'bottom'
    } else if (event.clientX <= tolerance) {
      return 'left'
    } else if (event.clientX >= window.innerWidth - tolerance) {
      return 'right'
    }

    return null
  }

  trackExitIntent(trigger, exitSide = null) {
    // For visibility_change and before_unload, only track once
    if ((trigger === 'visibility_change' || trigger === 'before_unload') && this.exitIntentTracked) {
      return
    }

    if (trigger === 'visibility_change' || trigger === 'before_unload') {
      this.exitIntentTracked = true
    }

    // Track final max scroll depth before exit
    this.trackMaxScrollDepth()

    ahoy.track('exit_intent', {
      trigger: trigger,
      exit_side: exitSide,
      page: 'home',
      time_on_page: Math.round((new Date() - this.pageLoadedAt) / 1000),
      max_scroll_depth: this.maxScrollDepth,
      device_type: this.getDeviceType()
    })
  }
}
